import { Button } from "@progress/kendo-react-buttons";
import { Input } from "@progress/kendo-react-inputs";
import { useEffect, useState } from "react";
import { useChangePasswordMutation, useGetUserInfoMutation } from "../../../api/userApi";

const GeneralSettings: React.FC = () => {
    const [email, setEmail] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
    const [feedbackType, setFeedbackType] = useState<"success" | "danger" | null>(null);

    const [getUserInfo] = useGetUserInfoMutation();
    const [changePassword, { isLoading: isChangingPassword }] = useChangePasswordMutation();

    useEffect(() => {
        const loadUserInfo = async () => {
            try {
                const userInfo = await getUserInfo().unwrap();
                setEmail(userInfo.email);
                setPhoneNumber((userInfo as { phoneNumber?: string | null }).phoneNumber ?? "");
            } catch {
                setFeedbackType("danger");
                setFeedbackMessage("Unable to load your account settings right now.");
            }
        };

        void loadUserInfo();
    }, [getUserInfo]);

    const handlePasswordChange = async () => {
        if (!currentPassword || !newPassword) {
            setFeedbackType("danger");
            setFeedbackMessage("Current password and new password are both required.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setFeedbackType("danger");
            setFeedbackMessage("New password and confirm password do not match.");
            return;
        }

        try {
            const result = await changePassword({ current_password: currentPassword, new_password: newPassword }).unwrap();
            if (!result.is_success) {
                setFeedbackType("danger");
                setFeedbackMessage(result.error_message ?? "Password change failed.");
                return;
            }

            setFeedbackType("success");
            setFeedbackMessage("Your password has been changed successfully.");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch {
            setFeedbackType("danger");
            setFeedbackMessage("Unable to change your password right now.");
        }
    };

    return (
        <div className="p-3">
            <div className="d-flex flex-column gap-3">
                <section>
                    <h3 className="h6 mb-3">General Profile</h3>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Email</label>
                            <Input value={email} onChange={event => setEmail(event.value as string)} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Phone Number</label>
                            <Input value={phoneNumber} onChange={event => setPhoneNumber(event.value as string)} />
                        </div>
                    </div>
                    <p className="text-muted small mt-2 mb-0">Profile save is managed by your account administrator.</p>
                </section>

                <section className="pt-2 border-top">
                    <h3 className="h6 mb-3">Change Password</h3>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Current Password</label>
                            <Input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.value as string)} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">New Password</label>
                            <Input type="password" value={newPassword} onChange={event => setNewPassword(event.value as string)} />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Confirm Password</label>
                            <Input type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.value as string)} />
                        </div>
                    </div>
                    <div className="mt-3 d-flex justify-content-end">
                        <Button themeColor="primary" onClick={handlePasswordChange} disabled={isChangingPassword}>
                            {isChangingPassword ? "Updating..." : "Change Password"}
                        </Button>
                    </div>
                </section>

                {feedbackMessage && feedbackType && (
                    <div className={`alert alert-${feedbackType} mb-0`} role="alert">
                        {feedbackMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GeneralSettings;
